import { SupportedChainId } from 'config/chains'
import { useConnection } from 'hooks/useConnection'
import { getChainNameFromId, getParsedChainId } from 'lib/utils/utils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import useParsedQueryString from './useParsedQueryString'
import usePrevious from './usePrevious'
import useSelectChain from './useSelectChain'

export default function useSyncChainQuery() {
  const { chainId, initialized, isConnected, isConnectionSettling } = useConnection()
  const navigate = useNavigate()
  const { search } = useLocation()
  const parsedQs = useParsedQueryString()

  const urlChainId = getParsedChainId(parsedQs)
  const previousUrlChainId = usePrevious(urlChainId)

  const selectChain = useSelectChain()

  // Track previous wallet chainId
  const [previousChainId, setPreviousChainId] = useState<number | undefined>(undefined)
  useEffect(() => {
    if (chainId && chainId !== previousChainId) setPreviousChainId(chainId)
  }, [chainId, previousChainId])

  const replaceURLChainParam = useCallback(() => {
    if (!chainId) return
    const chainName = getChainNameFromId(chainId)
    if (!chainName) return // avoid "?chain="
    const params = new URLSearchParams(search)
    params.set('chain', chainName)
    navigate({ search: `?${params.toString()}` }, { replace: true })
  }, [chainId, search, navigate])

  // Ensure these are booleans
  const chainQueryUnpopulated = urlChainId == null && chainId != null
  const chainChanged = chainId !== previousChainId
  const chainQueryStale = urlChainId !== chainId
  const chainQueryManuallyUpdated = urlChainId != null && urlChainId !== previousUrlChainId
  const hasHandledInitialUrl = useRef(false)
  const hasSyncedConnection = useRef(false)
  const pendingUrlChainId = useRef<number>()
  const [isSwitchRejected, setIsSwitchRejected] = useState(false)

  const requestUrlChain = useCallback(
    (targetChainId: number) => {
      pendingUrlChainId.current = targetChainId
      setIsSwitchRejected(false)
      selectChain(targetChainId as SupportedChainId).then((isSwitched) => {
        if (!isSwitched && pendingUrlChainId.current === targetChainId) setIsSwitchRejected(true)
      })
    },
    [selectChain]
  )

  useEffect(() => {
    if (!initialized || isConnectionSettling) return

    if (!isConnected) hasSyncedConnection.current = false

    if (pendingUrlChainId.current !== undefined) {
      if (isSwitchRejected) {
        pendingUrlChainId.current = undefined
        setIsSwitchRejected(false)
        replaceURLChainParam()
        return
      }
      if (chainId !== pendingUrlChainId.current && urlChainId === pendingUrlChainId.current) return
      pendingUrlChainId.current = undefined
    }

    const isNewConnection = isConnected && !hasSyncedConnection.current
    if (isNewConnection) hasSyncedConnection.current = true

    if (!hasHandledInitialUrl.current || isNewConnection) {
      hasHandledInitialUrl.current = true
      if (urlChainId != null && chainQueryStale) {
        requestUrlChain(urlChainId)
        return
      }
    }

    if (chainQueryUnpopulated) {
      // If there is no chain query param, set it to the current chain
      replaceURLChainParam()
    } else if (chainChanged && chainQueryStale) {
      // If the chain changed but the query param is stale, update to the current chain
      replaceURLChainParam()
    } else if (chainQueryManuallyUpdated && chainQueryStale && urlChainId != null) {
      // If the query param changed, and the chain didn't change, then activate the new chain
      requestUrlChain(urlChainId)
    }
  }, [
    chainId,
    initialized,
    isConnectionSettling,
    isConnected,
    isSwitchRejected,
    chainQueryUnpopulated,
    chainChanged,
    chainQueryStale,
    chainQueryManuallyUpdated,
    urlChainId,
    replaceURLChainParam,
    requestUrlChain,
  ])
}
